import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query"
import { apiFetch } from "./sdk"

/** Admin → Issues (backend lib/issues.ts): everything that failed and needs a person. */

export type IssueSeverity = "critical" | "high" | "normal"
export type IssueStatus = "open" | "resolved" | "ignored"
export type Issue = {
  id: string
  area: string
  kind: string
  severity: IssueSeverity
  status: IssueStatus
  title: string
  detail: string | null
  order_id: string | null
  cart_id: string | null
  customer_id: string | null
  email: string | null
  subscription_id: string | null
  amount: number | null
  transaction_id: string | null
  context: Record<string, unknown>
  occurrences: number
  first_seen_at: string
  last_seen_at: string
  retry_count: number
  last_retry_at: string | null
  last_retry_result: string | null
  resolved_at: string | null
  resolved_by: string | null
  resolution: string | null
  retry_label: string | null
}
export type PaymentBeingChecked = { id: string; cart_id: string; amount: number; email: string | null; transaction_id: string | null; result: string | null; last_error: string | null; created_at: string; checked_at: string | null }
export type IssueCounts = { open: number; critical: number; high: number; normal: number; by_area: Record<string, number> }
export type IssuesResp = { issues: Issue[]; count: number; counts: IssueCounts; payments_being_checked: PaymentBeingChecked[] }

export const AREA: Record<string, string> = {
  checkout: "Checkout",
  payment: "Payments",
  refund: "Refunds",
  subscription: "Subscriptions",
  recharge: "Recharge move",
  shipping: "Shipping",
  email: "Emails",
  sms: "Texts",
  gift_card: "Gift cards",
  draft_order: "Draft orders",
  order: "Orders",
  customer: "Customer accounts",
  webhook: "Webhooks",
  job: "Background jobs",
  ads: "Ad platforms",
}

export function useIssues(qs: string) {
  return useQuery({ queryKey: ["issues", qs], queryFn: () => apiFetch<IssuesResp>(`/admin/terify/issues?${qs}`), placeholderData: keepPreviousData, refetchInterval: 30_000 })
}

/** Open issue counts for the sidebar badge and the home banner. */
export function useIssueCounts(enabled: boolean) {
  return useQuery({
    queryKey: ["issues", "counts"],
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => (await apiFetch<IssuesResp>(`/admin/terify/issues?status=open&limit=1`)).counts,
  })
}

export function useIssueActions() {
  const qc = useQueryClient()
  const done = () => qc.invalidateQueries({ queryKey: ["issues"] })
  const retry = useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean; message: string; issue: Issue }>(`/admin/terify/issues/${id}/retry`, { method: "POST", body: "{}" }),
    onSettled: done,
  })
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: IssueStatus; note?: string }) =>
      apiFetch<{ issue: Issue }>(`/admin/terify/issues/${v.id}`, { method: "POST", body: JSON.stringify({ status: v.status, ...(v.note ? { note: v.note } : {}) }) }),
    onSettled: done,
  })
  return { retry, setStatus }
}
